/**
 * @file RichTextEditor.tsx
 * @description Лёгкий wysiwyg-редактор для описаний (v0.18.1, запрос
 * пользователя: форматирование в описании товара).
 *
 * Отличия от TiptapEditor блога: значение — HTML-строка (хранится прямо в
 * текстовом поле сущности, напр. Product.description), без картинок/видео/
 * callout'ов — только текстовое форматирование: H2/H3, жирный/курсив/
 * подчёркивание, списки, ссылка, undo/redo. Витрина рендерит HTML стилями
 * `.article-body` (как блог).
 */

import { Placeholder as PlaceholderExtension } from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Redo,
  Underline as UnderlineIcon,
  Undo,
} from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

interface RichTextEditorProps {
  /** HTML-строка (пустая = пустой редактор). */
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** id подписи поля: у contenteditable нет `<label htmlFor>`, имя даёт `aria-labelledby`. */
  labelledBy?: string;
}

function ToolButton({
  onClick,
  active,
  aria,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  aria: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={aria}
      title={aria}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      // Касание до 1024 — высота поля темы (`--control-h`, 48), на ПК — прежние 28 px.
      className={`inline-flex min-h-[var(--control-h,3rem)] min-w-[var(--control-h,3rem)] items-center justify-center rounded p-1.5 transition-colors hover:bg-surface-low lg:min-h-0 lg:min-w-0 ${
        active ? "bg-surface-low text-primary" : "text-on-surface-variant"
      }`}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, placeholder, disabled, labelledBy }: RichTextEditorProps) {
  // Идёт программная подстановка значения: правки «от редактора» в это время
  // не правки пользователя (см. эффект синхронизации ниже).
  const syncing = useRef(false);
  const editor = useEditor({
    extensions: [
      // Ссылка входит в StarterKit v3 — настраиваем её там. Отдельное
      // подключение давало «Duplicate extension names found: ['link']» (QA-5 D13).
      // TrailingNode выключен: он сам дописывает пустой абзац после списка
      // при первой же транзакции — описание «менялось» без пользователя.
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: { openOnClick: false, autolink: true },
        trailingNode: false,
      }),
      PlaceholderExtension.configure({ placeholder: placeholder ?? "Начните писать…" }),
    ],
    content: value || "",
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[160px] focus:outline-none px-4 py-3 prose-headings:font-bold prose-h2:text-xl prose-h3:text-lg prose-a:text-primary",
        // Подписанное поле — многострочное текстовое поле для скринридера.
        ...(labelledBy ? { role: "textbox", "aria-multiline": "true", "aria-labelledby": labelledBy } : {}),
      },
    },
    onUpdate({ editor: e }) {
      if (syncing.current) return;
      // Пустой документ отдаём пустой строкой, а не «<p></p>».
      onChange(e.isEmpty ? "" : e.getHTML());
    },
  });

  // Синхронизация внешнего значения (напр. загрузка товара после mount).
  // Подстановка — не правка пользователя: emitUpdate: false и на всякий
  // случай флаг `syncing` (дописанные плагинами транзакции внутри setContent
  // тоже не должны выглядеть правкой — ложный dirty, QA-5 D13).
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? "" : editor.getHTML();
    if (value !== current) {
      syncing.current = true;
      try {
        editor.commands.setContent(value || "", { emitUpdate: false });
      } finally {
        syncing.current = false;
      }
    }
  }, [editor, value]);

  // setEditable по умолчанию тоже эмитит update (второй аргумент `emitUpdate`):
  // без `false` открытие товара и каждое сохранение (поле блокируется на время
  // запроса) выглядели правкой описания — ложный dirty (QA-5 D13).
  useEffect(() => {
    if (editor && editor.isEditable !== !disabled) editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  if (!editor) return null;

  function promptLink() {
    const prev = editor!.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL ссылки (пусто — удалить):", prev ?? "");
    if (url === null) return;
    if (url === "") {
      editor!.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor!.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  }

  return (
    <div className="flex flex-col rounded-md border border-on-surface/20 bg-surface">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-on-surface/20 bg-surface-2 p-1.5">
        <ToolButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          aria="Заголовок"
        >
          <Heading2 className="h-4 w-4" />
        </ToolButton>
        <ToolButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
          aria="Подзаголовок"
        >
          <Heading3 className="h-4 w-4" />
        </ToolButton>
        <ToolButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          aria="Жирный"
        >
          <Bold className="h-4 w-4" />
        </ToolButton>
        <ToolButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          aria="Курсив"
        >
          <Italic className="h-4 w-4" />
        </ToolButton>
        <ToolButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("underline")}
          aria="Подчёркнутый"
        >
          <UnderlineIcon className="h-4 w-4" />
        </ToolButton>
        <ToolButton onClick={promptLink} active={editor.isActive("link")} aria="Ссылка">
          <LinkIcon className="h-4 w-4" />
        </ToolButton>
        <ToolButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          aria="Маркированный список"
        >
          <List className="h-4 w-4" />
        </ToolButton>
        <ToolButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          aria="Нумерованный список"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolButton>
        <span className="mx-1 h-4 w-px bg-on-surface/20" />
        <ToolButton onClick={() => editor.chain().focus().undo().run()} aria="Отменить">
          <Undo className="h-4 w-4" />
        </ToolButton>
        <ToolButton onClick={() => editor.chain().focus().redo().run()} aria="Повторить">
          <Redo className="h-4 w-4" />
        </ToolButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
